// ─── routePrefetch.js ────────────────────────────────────────────────
// Hover / focus / touch-based route chunk prefetching.
//
// Why:
//   Every admin page is code-split via React.lazy() in App.js. That keeps
//   the initial JS payload tiny (~360KB) but adds a ~200-800ms fetch
//   delay the FIRST time a user clicks on any given menu item — the
//   chunk has to travel from the CDN before React can even start
//   rendering the page.
//
// Fix:
//   Sidebar links call `prefetchRoute(to)` on mouseenter / focus / touch.
//   The chunk starts downloading BEFORE the user actually clicks, so by
//   the time the click fires, the module is already in the browser
//   cache and the page renders instantly.
//
// Notes:
//   - We de-dupe (`prefetched` Set) so hovering the same link 20 times
//     doesn't re-issue the network request.
//   - Errors are swallowed — a failed prefetch is fine; the real
//     import() at click-time will still work (or fail loudly).
//   - Keep this map in sync with the lazy() imports in App.js.
// ─────────────────────────────────────────────────────────────────────

const ROUTE_PRELOADERS = {
    // Common landing / auth (rarely prefetched but present for completeness)
    '/privacy':                       () => import('@/pages/Privacy'),

    // Customer routes
    '/customer':                      () => import('@/pages/customer/Home'),
    '/customer/home':                 () => import('@/pages/customer/Home'),
    '/customer/apply':                () => import('@/pages/customer/NewApplication'),
    '/customer/applications':         () => import('@/pages/customer/MyApplications'),
    '/customer/services':             () => import('@/pages/customer/ServicesPage'),
    '/customer/support':              () => import('@/pages/customer/SupportPage'),
    '/customer/complaints':           () => import('@/pages/customer/MyComplaints'),
    '/customer/subsidy':              () => import('@/pages/customer/SubsidyInfo'),
    '/customer/links':                () => import('@/pages/customer/QuickLinks'),
    '/customer/vendors':              () => import('@/pages/customer/VendorList'),
    '/customer/profile':              () => import('@/pages/customer/ProfilePage'),
    '/customer/learn':                () => import('@/pages/customer/SolarInfoPage'),
    '/customer/ai':                   () => import('@/pages/customer/AIChatPage'),
    '/customer/quotations':           () => import('@/pages/customer/QuotationsPage'),
    '/customer/survey':               () => import('@/pages/customer/SiteSurveyPage'),
    '/customer/generation':           () => import('@/pages/customer/GenerationLogPage'),
    '/customer/refer':                () => import('@/pages/customer/ReferPage'),
    '/customer/projects':             () => import('@/pages/customer/CustomerProjects'),
    '/customer/credit-report':        () => import('@/pages/customer/CustomerCreditReport'),
    '/customer/bbps':                 () => import('@/pages/BbpsPage'),
    '/customer/wallet':               () => import('@/pages/customer/WalletPage'),
    '/customer/loans':                () => import('@/pages/customer/CustomerLoans'),

    // Admin routes (order mirrors App.js)
    '/admin':                         () => import('@/pages/admin/AdminDashboard'),
    '/admin/dashboard':               () => import('@/pages/admin/AdminDashboard'),
    '/admin/leads':                   () => import('@/pages/admin/AdminLeads'),
    '/admin/quotations':              () => import('@/pages/admin/AdminQuotations'),
    '/admin/sale-invoices':           () => import('@/pages/admin/AdminSaleInvoices'),
    '/admin/cash-register':           () => import('@/pages/admin/AdminCashRegister'),
    '/admin/payment-center':          () => import('@/pages/admin/AdminPaymentCenter'),
    '/admin/applications':            () => import('@/pages/admin/AdminApplications'),
    '/admin/projects':                () => import('@/pages/admin/AdminProjects'),
    '/admin/credit-reports':          () => import('@/pages/admin/AdminCreditReports'),
    '/admin/credit-report':           () => import('@/pages/customer/CustomerCreditReport'),
    '/admin/bbps':                    () => import('@/pages/admin/AdminBbps'),
    '/admin/wallet':                  () => import('@/pages/admin/AdminWallet'),
    '/admin/complaints':              () => import('@/pages/admin/AdminComplaints'),
    '/admin/care-audit-log':          () => import('@/pages/admin/AdminCareAuditLog'),
    '/admin/customers':               () => import('@/pages/admin/AdminCustomers'),
    '/admin/pending-signups':         () => import('@/pages/admin/AdminPendingSignups'),
    '/admin/loans':                   () => import('@/pages/admin/AdminLoans'),
    '/admin/eko-settings':            () => import('@/pages/admin/AdminEkoSettings'),
    '/admin/redpay-settings':         () => import('@/pages/admin/AdminRedPaySettings'),
    '/admin/provider-router':         () => import('@/pages/admin/AdminProviderRouter'),
    '/admin/financial-reports':       () => import('@/pages/admin/AdminFinancialReports'),
    '/admin/subsidy':                 () => import('@/pages/admin/AdminSubsidyEditor'),
    '/admin/content':                 () => import('@/pages/admin/AdminContentEditor'),
    '/admin/site-surveys':            () => import('@/pages/admin/AdminSiteSurveys'),
    '/admin/staff':                   () => import('@/pages/admin/AdminStaff'),
    '/admin/access-bundles':          () => import('@/pages/admin/AdminAccessBundles'),
    '/admin/referrals':               () => import('@/pages/admin/AdminReferrals'),
    '/admin/attendance':              () => import('@/pages/admin/AdminAttendance'),
    '/admin/attendance-summary':      () => import('@/pages/admin/AdminAttendanceSummary'),
    '/admin/products':                () => import('@/pages/admin/AdminProducts'),
    '/admin/payments-report':         () => import('@/pages/admin/AdminPaymentsReport'),
    '/admin/reports':                 () => import('@/pages/admin/AdminReports'),
    '/admin/landing-cms':             () => import('@/pages/admin/AdminLandingCMS'),
    '/admin/login-events':            () => import('@/pages/admin/AdminLoginEvents'),
    '/admin/push-console':            () => import('@/pages/admin/AdminPushConsole'),
    '/admin/broadcast-news':          () => import('@/pages/admin/AdminBroadcastNews'),
    '/admin/hierarchy':               () => import('@/pages/admin/AdminHierarchy'),
    '/admin/my-team':                 () => import('@/pages/admin/MyTeamPage'),
    '/admin/vendor-team':             () => import('@/pages/vendor/VendorTeam'),
    '/admin/vendor-wallet':           () => import('@/pages/vendor/VendorWallet'),
    '/admin/my-wallet':               () => import('@/pages/admin/MyStaffWallet'),
    '/admin/territory-heatmap':       () => import('@/pages/admin/TerritoryHeatmap'),
    '/admin/my-attendance-history':   () => import('@/pages/staff/MyAttendanceHistory'),
    '/admin/saathi-bookings':         () => import('@/pages/admin/AdminSaathiBookings'),
    '/admin/staff-tracking':          () => import('@/pages/admin/AdminStaffTracking'),
    '/admin/my-attendance':           () => import('@/pages/staff/StaffAttendance'),
    '/admin/notifications':           () => import('@/pages/admin/AdminNotifications'),
    '/admin/view-messages':           () => import('@/pages/admin/AdminMessageOutbox'),
    '/admin/whatsapp-inbox':          () => import('@/pages/admin/WhatsAppInbox'),
    '/admin/nerochat-logs':           () => import('@/pages/admin/AdminNerochatLogs'),
    '/admin/whatsapp-broadcast':      () => import('@/pages/admin/AdminWhatsAppBroadcast'),
    '/admin/wapp-templates':          () => import('@/pages/admin/AdminWappTemplates'),
    '/admin/sla-config':              () => import('@/pages/admin/AdminSlaConfig'),
    '/admin/razorpay':                () => import('@/pages/admin/AdminRazorpay'),
    '/admin/mobile-app':              () => import('@/pages/admin/AdminMobileApp'),
    '/admin/subsidy-tracker':         () => import('@/pages/admin/AdminSubsidyTracker'),
    '/admin/deletion-requests':       () => import('@/pages/admin/AdminDeletionRequests'),
    '/admin/action-view':             () => import('@/pages/admin/AdminActionView'),
    '/admin/system-errors':           () => import('@/pages/admin/AdminSystemErrors'),
};

const prefetched = new Set();

/**
 * Kick off a background chunk fetch for the given route path.
 * Idempotent — the same route is only fetched once per session.
 *
 * @param {string} path — e.g. '/admin/leads' or '/admin/leads?tab=all'
 */
export function prefetchRoute(path) {
    if (!path || typeof path !== 'string') return;
    // Strip query & trailing slash so '/admin/leads?tab=x' matches '/admin/leads'
    const key = path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    if (prefetched.has(key)) return;
    const loader = ROUTE_PRELOADERS[key];
    if (!loader) return;
    prefetched.add(key);
    try {
        // Fire-and-forget; ignore rejections (they'll surface at real click)
        Promise.resolve(loader()).catch(() => {
            // Allow retry on real navigation if the network was momentarily bad
            prefetched.delete(key);
        });
    } catch {
        prefetched.delete(key);
    }
}

/**
 * Prefetch a curated list of the most-used admin routes after the app
 * has been idle. Called once from AdminLayout so navigating from the
 * dashboard to leads / applications / customers feels instant.
 */
export function prefetchAdminEssentials() {
    const essentials = [
        '/admin/leads',
        '/admin/applications',
        '/admin/customers',
        '/admin/complaints',
        '/admin/projects',
        '/admin/staff',
    ];
    const run = () => essentials.forEach(prefetchRoute);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 2500 });
    } else {
        setTimeout(run, 1500);
    }
}
